import { differenceInDays } from 'date-fns';

import { TradeEntity } from '@/common/types/entities';
import { Collision } from '@/features/collisions/types';
import { PdfScheduleFormat } from '@/features/scheduleExport/pdfExport/engine/pdf/types';
import type { ProjectScheduleDto } from '@/services/store/bulkApiClient';
import { SchedulerEventColorType } from '@/services/store/schedule/types/types';
import {
  convertDependencies,
  convertMilestones,
  convertOrders,
  convertPauses,
  convertWbsSections,
} from '@/services/store/utils';

import {
  PDFExportAnalyticsEventOrderColor,
  PDFExportAnalyticsEventProperties,
} from '../filter/analytics';
import { DinPaperFormat, PageOrientation } from './pdfExport/types';
import { ScheduleExportStore } from './scheduleExportStore';
import { ExportEntities, ExportKey, ExportSettingsInput, PdfExportType } from './types';

export function isGanttExport(exportType: ExportKey) {
  return (
    exportType === ExportKey.MS_PROJECT ||
    exportType === ExportKey.PRIMAVERA_P6 ||
    exportType === ExportKey.POWER_PROJECT
  );
}

export const getInitialExportSettings = (): ExportSettingsInput => ({
  advancedSettings: {
    customPdfHeadline: '',
    groupSequences: false,
    hideDependencies: false,
    hideWeekends: false,
    showCollisions: false,
    showTodayLine: false,
  },
  collapsedRowIds: new Set(),
  contributorGroups: [],
  eventColorType: SchedulerEventColorType.TRADE,
  logoSrc: null,
  printerSettings: {
    type: PdfExportType.PRINT,
    orientation: PageOrientation.LANDSCAPE,
    paperFormat: DinPaperFormat.A4,
    printOnMultiplePages: false,
  },
  timeRange: null,
  sections: [],
  totalSections: 0,
  tenantTradeVariations: [],
});

export const getExportOptions = (scheduleExportStore: ScheduleExportStore) => {
  const exportOptions = {
    type: scheduleExportStore.selectedExportType,
    timeRange: scheduleExportStore.timeRange
      ? {
          start: scheduleExportStore.timeRange.start.toISOString(),
          end: scheduleExportStore.timeRange.end.toISOString(),
        }
      : undefined,
    contributorGroups: scheduleExportStore.contributorGroups,
    trades: scheduleExportStore.tenantTradeVariations,
    wbsSections: scheduleExportStore.sections,
    groupByTradeSequenceInstances: !!scheduleExportStore.advancedSettings.groupSequences,
  };

  return exportOptions;
};

export const getExportEventProperties = (
  exportSettings: ExportSettingsInput,
  pdfScheduleFormat: PdfScheduleFormat,
): PDFExportAnalyticsEventProperties => {
  const timeSpanInDays = differenceInDays(pdfScheduleFormat.endDate, pdfScheduleFormat.startDate);
  return {
    orderColor: getAnalyticsViewTypeFromConfig(exportSettings.eventColorType),
    timeSpanInDays,
    filters: {
      companiesSelected: exportSettings.contributorGroups.length,
      rowsSelected: exportSettings.sections.length,
      tradesSelected: exportSettings.tenantTradeVariations.length,
    },
    viewSettings: {
      hideDependencies: exportSettings.advancedSettings.hideDependencies,
      hideWeekends: exportSettings.advancedSettings.hideWeekends,
      showCollisions: exportSettings.advancedSettings.showCollisions,
      showTodayLine: exportSettings.advancedSettings.showTodayLine,
    },
    format: {
      orientation: exportSettings.printerSettings.orientation,
      paperFormat: exportSettings.printerSettings.paperFormat,
      isSinglePage: !exportSettings.printerSettings.printOnMultiplePages,
    },
  };
};

export function getAnalyticsViewTypeFromConfig(
  eventColorType: SchedulerEventColorType,
): PDFExportAnalyticsEventOrderColor {
  if (eventColorType === SchedulerEventColorType.BASE_ACTUAL) return 'base_actual';
  if (eventColorType === SchedulerEventColorType.STATUS) return 'status';
  return 'trade';
}

export function getExportEntities(
  scheduleString: string | undefined | null,
  trades: Map<string, TradeEntity>,
): ExportEntities | null {
  try {
    const schedule = JSON.parse(scheduleString ?? '{}');
    if (!isProjectExportScheduleDto(schedule)) return null;

    return {
      collisions: convertCollisions(schedule.collisions),
      dependencies: convertDependencies(schedule.dependencies),
      milestones: convertMilestones(schedule.milestones),
      orders: convertOrders(schedule.orders),
      pauses: convertPauses(schedule.pauses),
      trades,
      wbsSections: convertWbsSections(schedule.wbsSections),
    };
  } catch {
    return null;
  }
}

const convertCollisions = (collisions: Collision[]): Map<string, Collision> => {
  return new Map(
    collisions.map((collision) => [collision.id, { ...collision, date: new Date(collision.date) }]),
  );
};

// TypeScript guard for sanity check. Not too strict but good enough
function isProjectExportScheduleDto(
  schedule: any,
): schedule is ProjectScheduleDto & { collisions: Collision[] } {
  return (
    schedule &&
    typeof schedule === 'object' &&
    Array.isArray(schedule.collisions) &&
    Array.isArray(schedule.orders) &&
    Array.isArray(schedule.dependencies) &&
    Array.isArray(schedule.milestones) &&
    Array.isArray(schedule.wbsSections) &&
    Array.isArray(schedule.pauses) &&
    Array.isArray(schedule.calendars)
  );
}
